import { Eyebrow, PageHero, PrimaryLink, WhitePill } from "./components"
import { usePublicPrograms } from "./api"
import { GlobeIcon, GraduationCapIcon, MicIcon, MoneyBagIcon, OnlineLearningIcon, StoreIcon } from "./icons"

const LEARNING_HUB = [
  {
    title: "Homework & tutoring",
    body: "After-school support in core subjects, exam prep, and reading circles.",
    icon: <OnlineLearningIcon className="h-3.5 w-3.5" />,
  },
  {
    title: "Confidence & communication",
    body: "Public speaking, debate, and storytelling — the soft skills schools rarely teach.",
    icon: <MicIcon className="h-3.5 w-3.5" />,
  },
  {
    title: "Digital literacy",
    body: "Typing, safe internet use, basic productivity tools, and an intro to coding.",
    icon: <GlobeIcon className="h-3.5 w-3.5" />,
  },
  {
    title: "Creative expression",
    body: "Writing, art, and music clubs that build curiosity and belonging.",
    icon: <GraduationCapIcon className="h-3.5 w-3.5" />,
  },
]

const SKILLS_CENTER = [
  {
    title: "Trades workshop",
    body: "Carpentry, electrical, and repair basics — hands-on with real tools and mentors.",
    icon: <StoreIcon className="h-3.5 w-3.5" />,
  },
  {
    title: "Fashion & tailoring",
    body: "Pattern-making, sewing, and small-batch production for local markets.",
    icon: <GraduationCapIcon className="h-3.5 w-3.5" />,
  },
  {
    title: "Agro-enterprise",
    body: "Backyard farming, poultry, and the business of turning produce into income.",
    icon: <GlobeIcon className="h-3.5 w-3.5" />,
  },
  {
    title: "Food & hospitality",
    body: "Baking, catering, and food-safety fundamentals for micro-enterprise.",
    icon: <MoneyBagIcon className="h-3.5 w-3.5" />,
  },
]

function ProgramBlock({
  label,
  title,
  body,
  image,
  imageAlt,
  features,
  flip = false,
}: {
  label: string
  title: React.ReactNode
  body: string
  image: string
  imageAlt: string
  features: readonly { title: string; body: string; icon: React.ReactNode }[]
  flip?: boolean
}) {
  return (
    <div className="space-y-4 lg:space-y-[50px]">
      <div className="grid items-center gap-4 lg:grid-cols-2 lg:gap-16">
        <div className={flip ? "lg:order-2" : ""}>
          <Eyebrow dot>{label}</Eyebrow>
          <h2 className="mt-2 font-display text-[24px] font-bold leading-none text-ink sm:text-[40px] lg:mt-4 lg:text-[48px] lg:leading-tight">
            {title}
          </h2>
          <p className="mt-4 text-[15px] leading-[22px] text-ink/80 lg:mt-5 lg:text-lg lg:leading-relaxed">{body}</p>
        </div>
        <img
          src={image}
          alt={imageAlt}
          className={`h-[200px] w-full rounded-2xl object-cover ${flip ? "lg:order-1" : "order-first lg:order-none"} lg:h-[499px]`}
        />
      </div>
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4 lg:gap-[10px]">
        {features.map((feature) => (
          <article key={feature.title} className="flex min-h-[204px] flex-col rounded-xl bg-white p-4 lg:h-[242px] lg:min-h-0 lg:rounded-[20px] lg:p-6">
            <span className="grid h-6 w-6 place-items-center rounded-full bg-forest-700 text-white">{feature.icon}</span>
            <div className="mt-auto">
              <h3 className="text-sm font-semibold text-ink lg:text-base">{feature.title}</h3>
              <p className="mt-2 text-xs leading-relaxed text-ink/70 lg:mt-3 lg:text-[15px]">{feature.body}</p>
            </div>
          </article>
        ))}
      </div>
    </div>
  )
}

function ActiveProgramsSection() {
  const { data } = usePublicPrograms()
  if (!data || data.length === 0) return null

  return (
    <section className="bg-cream-50 py-16 lg:py-20">
      <div className="public-container">
        <Eyebrow dot>Active programs</Eyebrow>
        <h2 className="mt-5 font-display text-3xl font-bold text-ink sm:text-4xl">What is running now.</h2>
        <div className="mt-8 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {data.map((program) => (
            <article key={program.id} className="rounded-2xl border border-ink/10 bg-white p-5 shadow-sm">
              <p className="text-xs font-semibold uppercase tracking-wider text-forest-700">{program.code}</p>
              <h3 className="mt-3 text-xl font-semibold text-ink">{program.name}</h3>
              <p className="mt-3 text-sm leading-6 text-ink/70">{program.description ?? "A Redempta program supporting children and communities."}</p>
            </article>
          ))}
        </div>
      </div>
    </section>
  )
}

export function ProgramsPage() {
  return (
    <>
      <PageHero
        eyebrow="Programs - Coming Online"
        title={<>Two halves of one <span className="text-forest-700">long game.</span></>}
        lede="Scholarships get children into the classroom. Our Learning Hub and Skills Center make sure they walk out of it ready — for university, for work, for building something of their own."
        eyebrowDot
      />

      <section className="bg-cream-50 py-[66px] lg:pb-[60px] lg:pt-[70px]">
        <div className="public-container space-y-10 lg:space-y-[70px]">
          <ProgramBlock
            label="01 - Learning Hub"
            title={<>Where curiosity <span className="text-forest-700">catches fire.</span></>}
            body="An after-school space for academic support, communication, and digital fluency. Built for our scholars first, then opened to any child in the neighborhood who wants in."
            image="/images/pages/programs-hub.jpg"
            imageAlt="Children studying together in the Learning Hub"
            features={LEARNING_HUB}
          />
          <ProgramBlock
            label="02 - Skills Center"
            title={<>Hands that can <span className="text-forest-700">build a living.</span></>}
            body="A vocational track that pairs trades with entrepreneurship. Not every path is university — and every path deserves dignity, tools, and a real market to sell into."
            image="/images/pages/programs-skills.jpg"
            imageAlt="A trainee working with tools in the Skills Center"
            features={SKILLS_CENTER}
            flip
          />
        </div>
      </section>

      <ActiveProgramsSection />

      <section className="bg-cream-100 py-[67px] lg:py-[59.5px]">
        <div className="public-container">
          <div className="flex flex-col gap-8 rounded-none bg-transparent p-0 text-center lg:items-center lg:gap-[50px]">
            <div className="max-w-[884px]">
              <h2 className="font-display text-[30px] font-bold leading-tight text-ink sm:text-[40px] lg:text-[48px] lg:leading-none">
                These programs open as we grow.
              </h2>
              <p className="mt-4 text-lg leading-relaxed text-ink/80 lg:mt-2.5 lg:leading-[1.3]">
                Every new sponsor unlocks a little more of this vision. Start with a scholarship — and help us break ground on the hub.
              </p>
            </div>
            <div className="programs-cta-actions flex flex-wrap items-center justify-center gap-4">
              <PrimaryLink to="/sponsor">Sponsor a Child</PrimaryLink>
              <WhitePill to="/contact">Partner with us</WhitePill>
            </div>
          </div>
        </div>
      </section>
    </>
  )
}
